import { faSearch, faXmark } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconButton, Input, Option, Select, Stack } from "@mui/joy";
import { type MRT_Row, type MRT_TableInstance } from "material-react-table";
import { type Dispatch, memo, type SetStateAction, useCallback } from "react";
import { EntitiesSwitch, ExportAsCsvButton } from "components/base";
import { DataRoomCreateButton } from "features/dataRoomCreation";
import {
  DataRoomType,
  dataRoomTypeCardPresentation,
  getDataRoomTypeFromTypename,
} from "models/dataRoom";
import DataRoomsListFilter from "./DataRoomsListFilter";
import {
  type DataRoomListFilter,
  type DataRoomsListItem,
  type SelectedView,
} from "./models";

interface DataRoomsListToolbarProps {
  isTable: boolean;
  table?: MRT_TableInstance<DataRoomsListItem>;
  handleSelectView: (value: SelectedView | null) => void;
  selectedView: SelectedView;
  typeFilterKey: DataRoomType | "All";
  setTypeFilterKey: Dispatch<SetStateAction<DataRoomType | "All">>;
  searchValue: string;
  filterKey: DataRoomListFilter["key"];
  setFilterKey: (value: SetStateAction<DataRoomListFilter["key"]>) => void;
  onSearchChange: (value: string) => void;
  hasData: boolean;
}

const getHeaders = () => ["Name", "Type", "Creation date", "Created by"];

const DataRoomsListToolbar = memo<DataRoomsListToolbarProps>(
  ({
    isTable,
    table,
    handleSelectView,
    selectedView,
    filterKey,
    setFilterKey,
    typeFilterKey,
    setTypeFilterKey,
    searchValue,
    onSearchChange,
    hasData,
  }) => {
    const isExportEnabled = isTable && hasData;
    const getTransformedRows = useCallback(() => {
      return table?.getPaginationRowModel().rows.map((row) => {
        const { name, createdAt, __typename } = row.original;
        const ownerEmail = row.original.owner?.email || "";
        const dataRoomType = getDataRoomTypeFromTypename[__typename!];
        const { title: dataRoomTypeTitle } =
          dataRoomTypeCardPresentation[dataRoomType];

        const formattedDate = createdAt
          ? new Date(createdAt).toLocaleString()
          : "-";
        return [name, dataRoomTypeTitle, formattedDate, ownerEmail];
      });
    }, [table]);
    return (
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ px: isTable ? 2 : 3, py: isTable ? 2 : 3 }}
        width="100%"
      >
        <Stack direction="row">
          <EntitiesSwitch
            onChange={handleSelectView}
            selectedView={selectedView}
          />
          <DataRoomsListFilter
            filterKey={filterKey}
            setFilterKey={setFilterKey}
          />
          <Select
            defaultValue={typeFilterKey as "All"}
            onChange={(event, value) => {
              setTypeFilterKey(value as DataRoomType | "All");
            }}
          >
            <Option value="all">All</Option>
            <Option value={DataRoomType.MediaInsights}>MDCR</Option>
            <Option value={DataRoomType.DataScience}>Advanced Analytics</Option>
          </Select>
          <Input
            endDecorator={
              searchValue ? (
                <IconButton onClick={() => onSearchChange("")}>
                  <FontAwesomeIcon icon={faXmark} />
                </IconButton>
              ) : null
            }
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search data clean rooms"
            startDecorator={<FontAwesomeIcon icon={faSearch} />}
            sx={{ minWidth: "280px" }}
            value={searchValue}
          />
        </Stack>
        <Stack direction="row">
          {isExportEnabled ? (
            <ExportAsCsvButton
              fileName="data-clean-rooms.csv"
              getHeaders={getHeaders}
              getRows={getTransformedRows}
            />
          ) : null}
          <DataRoomCreateButton />
        </Stack>
      </Stack>
    );
  }
);

export default DataRoomsListToolbar;
